import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [me, setMe] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const cfg = await api.get("/auth/config");
      setAuthEnabled(Boolean(cfg.data?.enabled));

      const meRes = await api.get("/me");
      setMe(meRes.data);
    } catch (e) {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    await api.post("/auth/logout");
    setMe(null);
  }

  useEffect(() => {
    refresh();
  }, []);

  const value = useMemo(
    () => {
      const roles = new Set(me?.roles?.length ? me.roles : [me?.role].filter(Boolean));
      const roleAccess = me?.role_access || [];
      const hasAccess = (pages = [], permissions = []) => {
        if (!roleAccess.length) return false;
        return roleAccess.some((role) => {
          if (role?.status === "disabled") return false;
          const rolePages = role?.pages || [];
          const rolePerms = role?.permissions || [];
          const pageMatch = !pages.length || pages.some((page) => rolePages.includes(page));
          const permMatch = !permissions.length || permissions.some((perm) => rolePerms.includes(perm));
          return pageMatch && permMatch;
        });
      };
      return ({
      loading,
      authEnabled,
      me,
      refresh,
      logout,
      roles,
      roleAccess,
      hasAccess,
      isAdmin: roles.has("admin"),
      isMod: roles.has("admin") || roles.has("mod"),
    });
    },
    [loading, authEnabled, me],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
